// Browser compatibility and environment detection utilities

export interface BrowserInfo {
  name: string;
  version: string;
  isSupported: boolean;
  missingFeatures: string[];
}

export interface EnvironmentInfo {
  isHttps: boolean;
  isLocalhost: boolean;
  isProduction: boolean;
  canUseMediaDevices: boolean;
  userAgent: string;
}

/**
 * Detect browser and check compatibility for transcription features
 */
export function detectBrowser(): BrowserInfo {
  const userAgent = navigator.userAgent;
  let browserName = 'Unknown';
  let browserVersion = 'Unknown';
  const missingFeatures: string[] = [];

  // Detect browser
  if (userAgent.includes('Chrome')) {
    browserName = 'Chrome';
    const match = userAgent.match(/Chrome\/(\d+)/);
    browserVersion = match ? match[1] : 'Unknown';
  } else if (userAgent.includes('Firefox')) {
    browserName = 'Firefox';
    const match = userAgent.match(/Firefox\/(\d+)/);
    browserVersion = match ? match[1] : 'Unknown';
  } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    browserName = 'Safari';
    const match = userAgent.match(/Version\/(\d+)/);
    browserVersion = match ? match[1] : 'Unknown';
  } else if (userAgent.includes('Edge')) {
    browserName = 'Edge';
    const match = userAgent.match(/Edge\/(\d+)/);
    browserVersion = match ? match[1] : 'Unknown';
  }

  // Check required features
  if (!navigator.mediaDevices) {
    missingFeatures.push('MediaDevices API');
  }
  
  if (!navigator.mediaDevices?.getUserMedia) {
    missingFeatures.push('getUserMedia');
  }
  
  if (typeof MediaRecorder === 'undefined') {
    missingFeatures.push('MediaRecorder API');
  }

  // Check audio codec support
  if (typeof MediaRecorder !== 'undefined') {
    if (!MediaRecorder.isTypeSupported('audio/webm')) {
      if (!MediaRecorder.isTypeSupported('audio/mp4')) {
        if (!MediaRecorder.isTypeSupported('audio/mpeg')) {
          missingFeatures.push('Audio recording codecs');
        }
      }
    }
  }

  const isSupported = missingFeatures.length === 0;

  return {
    name: browserName,
    version: browserVersion,
    isSupported,
    missingFeatures
  };
}

/**
 * Get environment information
 */
export function getEnvironmentInfo(): EnvironmentInfo {
  const isHttps = location.protocol === 'https:';
  const isLocalhost = location.hostname === 'localhost' || 
                     location.hostname === '127.0.0.1' ||
                     location.hostname.includes('localhost');
  const isProduction = !isLocalhost && import.meta.env.PROD;
  const canUseMediaDevices = isHttps || isLocalhost;

  return {
    isHttps,
    isLocalhost, 
    isProduction,
    canUseMediaDevices,
    userAgent: navigator.userAgent
  };
}

/**
 * Show user-friendly compatibility message
 */
export function getCompatibilityMessage(): string {
  const browser = detectBrowser();
  const env = getEnvironmentInfo();

  if (!browser.isSupported) {
    return `Browser tidak mendukung fitur transcription. Missing: ${browser.missingFeatures.join(', ')}. Gunakan Chrome, Firefox, atau Safari terbaru.`;
  }

  if (!env.canUseMediaDevices) {
    return 'Fitur transcription membutuhkan HTTPS. Silakan akses melalui domain yang aman atau localhost untuk testing.';
  }

  return 'Browser supported untuk transcription.';
}

/**
 * Log detailed compatibility info for debugging
 */
export function logCompatibilityInfo(): void {
  const browser = detectBrowser();
  const env = getEnvironmentInfo();
  
  console.group('🔍 Browser Compatibility Check');
  console.log('Browser:', `${browser.name} ${browser.version}`);
  console.log('Environment:', {
    isHttps: env.isHttps,
    isLocalhost: env.isLocalhost,
    isProduction: env.isProduction,
    canUseMediaDevices: env.canUseMediaDevices
  });
  console.log('Supported:', browser.isSupported);
  if (!browser.isSupported) {
    console.warn('Missing features:', browser.missingFeatures);
  }
  console.log('User Agent:', env.userAgent);
  console.groupEnd();
}